import { useState, useEffect } from 'react';
import ProjectsPage from './pages/ProjectsPage';
import TestimonialsPage from './pages/TestimonialsPage';
import ContactPage from './pages/ContactPage';
import { useSEO } from './useSEO';

function navigate(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const approach = [
  {
    heading: 'Collaborative',
    text: 'We start with your vision. I work side-by-side with you to understand your family\'s unique rhythm, ensuring the systems we build are intuitive, sustainable, and tailored specifically to how you move through your home.',
  },
  {
    heading: 'Iterative',
    text: 'Evolution is key. I provide ongoing support to sharpen your systems, ensuring your home stays organised even as your needs change. We refine the details until we find your perfect flow.',
  },
];

const outcomes = [
  {
    title: '10+ Homes Transformed',
    body: 'Creating calm out of household chaos.',
  },
  {
    title: 'Hundreds of Hours Reclaimed',
    body: 'Giving busy families their weekends back.',
  },
  {
    title: 'Personalised Systems',
    body: 'Tailored routines that stick for the long term.',
  },
];

const testimonials = [
  '"Working with Ivy was a total game-changer for our family. Before, our mornings were pure chaos and the playroom was a \'no-go\' zone."\n\nThey didn\'t just tidy up; they built a Household Management system that actually works for us. Now, the kids know exactly where their gear goes, and my daily to-do list feels manageable for the first time in years. It feels like our home finally has room to breathe again!',
  '"I used to spend my entire weekend playing catch-up with the house, but Ivy completely flipped the script. They didn\'t just organise our cupboards; they restructured how our entire household functions.\n\nFrom streamlining our laundry routine to setting up a system for domestic duties that everyone can follow, the \'mental load\' is finally gone. I\'ve gone from feeling like a constant manager of chaos to actually enjoying my home again. Truly the best investment I\'ve made for my sanity!"',
];

const faqs = [
  {
    question: 'Where Do We Begin?',
    answer:
      'First things first, do not be nervous. We will start with a free consultation to determine your design needs and whether we will make great collaborators.',
  },
  {
    question: 'What Are My Prices?',
    answer:
      'Pricing is based on both the project scope and your desired timeline. I promise fair rates and will always require your approval before we begin.',
  },
  {
    question: 'How Long Does an Engagement Last?',
    answer:
      'It depends on what you need! A quick reset might only take a session or two, while a full household overhaul can span a few weeks. You\'re always in control of the timeline, and we\'ll check in along the way to make sure it\'s working for you.',
  },
];

function HomePage() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ProfessionalService',
        '@id': 'https://www.resetandrun.com.au/#business',
        name: 'Reset & Run',
        image: 'https://www.resetandrun.com.au/ivy-portrait.jpg',
        url: 'https://www.resetandrun.com.au/',
        telephone: '0432447084',
        email: 'IvyResets@gmail.com',
        description: 'Professional home organiser and household manager helping busy families declutter, build sustainable systems, and reclaim their time.',
        areaServed: 'Australia',
      },
      {
        '@type': 'WebSite',
        '@id': 'https://www.resetandrun.com.au/#website',
        name: 'Reset & Run',
        url: 'https://www.resetandrun.com.au/',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  useSEO({
    title: 'Reset & Run | Home Organiser & Household Manager',
    description: 'Ivy is a professional home organiser and household manager helping busy families declutter, build sustainable systems, and reclaim their time.',
    path: '/',
    schema: homeSchema,
  });
  return (
    <>
      <header className="hero">
        <div className="hero__content">
          <div className="hero__copy">
            <h1>Hey, I&apos;m Ivy.</h1>
          </div>
          <div className="hero__portraitWrap">
            <img
              className="hero__portrait"
              src="/ivy-portrait.jpg"
              alt="Ivy from Reset and Run"
            />
          </div>
          <div className="hero__intro">
            <p>
              I&apos;m a Home Organiser and Household Manager. I declutter, structure
              living spaces, and handle domestic tasks to make life easier.
            </p>
            <a href="#contact" className="button button--salmon">Work with Me</a>
          </div>
        </div>
      </header>

      <main>
        <section className="approach" id="approach">
          <div className="approach-layout">
            <div className="approach-image" aria-hidden="true" style={{backgroundImage: `linear-gradient(rgba(35, 38, 58, 0.08), rgba(35, 38, 58, 0.08)), url(${process.env.PUBLIC_URL}/IKEA_Trofast_woodlandends.png)`}} />
            <div className="approach-copy">
              <h3>My Approach</h3>
              {approach.map((item) => (
                <div key={item.heading}>
                  <h4>{item.heading}</h4>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="outcomes-dark">
          <div className="outcomes-dark__track">
            {[...outcomes, ...outcomes].map((item, i) => (
              <article className="outcomes-dark__card" key={i}>
                <h2>{item.title}</h2>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="testimonials" id="testimonials">
          <div className="section-heading">
            <h3>What Clients are Saying</h3>
          </div>
          <div className="testimonial-grid">
            {testimonials.map((quote) => (
              <blockquote className="testimonial-card" key={quote}>
                <p>{quote}</p>
              </blockquote>
            ))}
          </div>
        </section>

        <section className="contact-panel" id="contact">
          <div className="contact-panel__copy">
            <h3>Get in Touch!</h3>
            <p>Schedule a free 1:1 introductory session with me to discuss your home organisation needs.</p>
          </div>
          <a className="button contact-panel__button" href="mailto:IvyResets@gmail.com">
            Book Consultation
          </a>
        </section>

        <section className="faq-section" id="faq">
          <h2 className="faq-section__title">FAQ</h2>
          <div className="faq-layout">
            <div className="faq-list">
              {faqs.map((item) => (
                <details className="faq-item" key={item.question}>
                  <summary>
                    <span>{item.question}</span>
                    <span className="faq-item__icon" aria-hidden="true">+</span>
                  </summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
            <div className="faq-image" aria-hidden="true">
              <div className="faq-image__card" style={{backgroundImage: `linear-gradient(rgba(35, 38, 58, 0.08), rgba(35, 38, 58, 0.08)), url(${process.env.PUBLIC_URL}/IMG_5175.jpg)`}} />
            </div>
          </div>
        </section>

        <footer className="site-footer">
          <div className="site-footer__curve" aria-hidden="true" />
          <div className="site-footer__grid">
            <div className="site-footer__brand">Ivy</div>
            <div className="site-footer__details">
              <p>0432 447 084</p>
              <p>IvyResets@gmail.com</p>
              <p>Reset and Run</p>
            </div>
            <div className="site-footer__details">
              <p>Schedule a free 1:1 introductory session.</p>
              <p>Personalised home organisation for busy families.</p>
            </div>
          </div>
          <p className="site-footer__disclaimer">Website created by <a href="https://www.hamilton365.com.au" target="_blank" rel="noopener noreferrer">Hamilton365</a></p>
        </footer>
      </main>
    </>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    setPath(window.location.pathname);
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navTo(p) {
    setMenuOpen(false);
    navigate(p);
  }

  let page;
  if (path === '/projects') page = <ProjectsPage />;
  else if (path === '/testimonials') page = <TestimonialsPage />;
  else if (path === '/contact') page = <ContactPage />;
  else page = <HomePage />;

  return (
    <div className="site-shell">
      <nav className="site-nav" aria-label="Primary">
        <button className="site-nav__brand site-nav__brand--btn" onClick={() => navTo('/')}>Reset &amp; Run</button>
        <button className="button button--nav site-nav__mobile-cta" onClick={() => navTo('/contact')}>Book Consultation</button>
        <button
          className="site-nav__burger"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span /><span /><span />
        </button>
      </nav>

      {menuOpen && (
        <div className="mobile-menu" role="navigation" aria-label="Mobile">
          <button onClick={() => navTo('/projects')}>Projects</button>
          <a href="/#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
          <button onClick={() => navTo('/testimonials')}>Testimonials</button>
          <button onClick={() => navTo('/contact')}>Contact Me</button>
        </div>
      )}

      {page}
    </div>
  );
}

export default App;

